import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Shield, Users, HelpCircle, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const benefits = [
  { title: "Proven Curriculum Support", desc: "Access to our play-way, Montessori-inspired academic syllabus, daily schedules, worksheets, and teacher manuals.", icon: Award, color: "#FDE8E9", ic: "#C8161D" },
  { title: "Staff & Teacher Training", desc: "Initial induction and periodic curriculum refresher training workshops for all teachers and coordinators.", icon: Users, color: "#F3F9E2", ic: "#9DC41E" },
  { title: "Operational & Setup Support", desc: "Detailed specifications for layout planning, childproofing, color schemes, and play equipment selection.", icon: Shield, color: "#E0F2FE", ic: "#0284c7" },
  { title: "Marketing & Launch Guidance", desc: "Assistance with regional advertising layouts, social media marketing designs, and branch launch kits.", icon: HelpCircle, color: "#FEF9C3", ic: "#ca8a04" }
];

const expectations = [
  "Minimum 1,500 – 2,500 sq. ft. of built-up area (preferably ground floor)",
  "Adequate safe outdoor play zone / garden space",
  "Prime, easily accessible residential locality in your city",
  "Hygienic washrooms, smart classroom layouts, and security setups",
  "Passion for early childhood education and child welfare"
];

const initialForm = {
  name: '',
  phone: '',
  email: '',
  city: '',
  state: '',
  proposedLocation: '',
  space: '',
  occupation: '',
  investment: '',
  message: '',
  consent: false
};

export default function Franchise() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Applicant name is required";
    if (!form.phone.trim() || !/^\+?[0-9\s-]{10,15}$/.test(form.phone)) e.phone = "Valid phone is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.space.trim()) e.space = "Available space details are required";
    if (!form.consent) e.consent = "You must agree to data processing";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1200);
  };

  const handleChange = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: val });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div>
      <PageHero 
        title="Grow With a Trusted Preschool Vision" 
        subtitle="Happykidz Preschool is building a future-focused early-learning network centered on joyful education, child safety, teacher support, parent trust, and consistent quality." 
        breadcrumb="Franchise" 
      />

      {/* Why Happykidz Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Partnership</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Why Partner With Us?</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">We assist franchise owners with setup details, equipment, staff selections, and operational guidelines.</p>
          </motion.div>

          <motion.div 
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {benefits.map((ben, i) => (
              <motion.div 
                key={i} 
                variants={fadeUp} 
                className="cartoon-card bg-white rounded-3xl p-8 border border-gray-100 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: ben.color }}>
                    <ben.icon size={26} style={{ color: ben.ic }} />
                  </div>
                  <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-3">{ben.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{ben.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Wave fill="#F8FAFC" bg="white" />

      {/* Infrastructure expectations */}
      <section className="py-20 bg-[#F8FAFC] polka-dots">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="bg-white rounded-3rem p-8 md:p-12 shadow-md border border-gray-100" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="section-label mb-4">Expectations</span>
            <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-6">Space &amp; Infrastructure Requirements</h3>
            <p className="text-gray-600 leading-relaxed mb-6 font-poppins text-sm">
              To keep our branding and safety standards uniform, every Happykidz branch must satisfy these primary layout expectations:
            </p>
            <div className="space-y-4">
              {expectations.map((exp, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-[#F8FAFC] rounded-2xl px-5 py-4 border border-gray-50">
                  <CheckCircle className="text-[#9DC41E] flex-shrink-0" size={18} />
                  <span className="text-gray-600 text-sm font-poppins">{exp}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Wave fill="white" bg="#F8FAFC" />

      {/* Franchise Enquiry Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Enquiry Form</span>
            <h2 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mt-3">Franchise Opportunity Enquiry</h2>
          </motion.div>

          <div className="bg-[#F8FAFC] rounded-3rem p-8 md:p-12 shadow-lg border border-gray-100">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-[#F3F9E2] rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-[#9DC41E]" />
                </div>
                <h3 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-3">Enquiry Submitted!</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Thank you for your interest in partnering with Happykidz Preschool. Our expansion desk will review your details and contact you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Applicant Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} className="input-field" placeholder="Full name" />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Phone Number *</label>
                    <input name="phone" value={form.phone} onChange={handleChange} className="input-field" placeholder="e.g. +91 99999 99999" />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Email Address *</label>
                    <input name="email" type="email" value={form.email} onChange={handleChange} className="input-field" placeholder="your@email.com" />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Current Occupation</label>
                    <input name="occupation" value={form.occupation} onChange={handleChange} className="input-field" placeholder="Your occupation" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Target City *</label>
                    <input name="city" value={form.city} onChange={handleChange} className="input-field" placeholder="City" />
                    {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Target State</label>
                    <input name="state" value={form.state} onChange={handleChange} className="input-field" placeholder="State" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Proposed Space Area (sq. ft.) *</label>
                    <input name="space" value={form.space} onChange={handleChange} className="input-field" placeholder="e.g. 2000 sq ft" />
                    {errors.space && <p className="text-red-500 text-xs mt-1">{errors.space}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Proposed Location Address</label>
                    <input name="proposedLocation" value={form.proposedLocation} onChange={handleChange} className="input-field" placeholder="Address/Locality" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Message or Special Inquiries</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} className="input-field resize-none" placeholder="Provide any additional details..." />
                </div>

                <div className="flex items-start gap-2.5">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} className="mt-1" id="consent-check" />
                  <label htmlFor="consent-check" className="text-xs text-gray-500 leading-relaxed">
                    I agree to Happykidz Preschool contacting me regarding franchise opportunities.
                  </label>
                </div>
                {errors.consent && <p className="text-red-500 text-xs">{errors.consent}</p>}

                <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-base">
                  {loading ? "Submitting Enquiry..." : "Submit Franchise Enquiry"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
